import type { ExternalRequestDetail, ExternalRequestMeta, RequestServiceStatus, RequestSummary } from '../../types';
import { formatDate } from '../../utils/formatters';

/**
 * Metadata stamped on a request-sourced case.
 *
 * The list summary already carries everything we need to link the case back to
 * the request; the detail fetch only adds the provider and the request content.
 * Content is optional, so when that fetch fails we still stamp the summary and
 * let the user carry on.
 */
export function requestMetaFromSummary(
  summary: RequestSummary,
  detail?: ExternalRequestDetail | null,
): Record<string, unknown> {
  return {
    source: 'request',
    request_provider: detail?.provider || 'nextrequest',
    external_request_id: detail?.external_id || summary.external_id,
    request_number: detail?.number || summary.number || undefined,
    request_status: detail?.status ?? summary.status,
    requester: detail?.requester ?? summary.requester,
    due_date: detail?.due_date ?? summary.due_date,
  };
}

/** Said under the picker when the server has more matches than it sent, else null. */
export function requestPickerFooter(shown: number, more: boolean | undefined): string | null {
  if (!more) return null;
  return `Showing the newest ${shown}. Type more of the number to narrow the list.`;
}

/**
 * Why the "From NextRequest" tab is grayed out, or null when it can be used
 * (FB-1007 #7). `status` null = still checking.
 */
export function requestTabBlockedReason(status: RequestServiceStatus | null): string | null {
  if (status === null) return 'Checking the NextRequest connection…';
  switch (status.state) {
    case 'connected':
      return null;
    case 'disabled':
      return 'NextRequest is disabled. Enable it in Global Settings → Integrations.';
    case 'not_configured':
      return 'NextRequest is not set up for this deployment.';
    default:
      return 'NextRequest is not connected. Connect it in Global Settings → Integrations.';
  }
}

/** One labelled line of the case page's "Source request" view (FB-1007 #8). */
export interface SourceRequestView {
  title: string;
  rows: { label: string; value: string }[];
  text: string;
  url: string | null;
  /** The live fetch failed: showing what the case stored when it was created. */
  stale: boolean;
}

/**
 * The source request of a case: the live request when it could be fetched,
 * else what the case stored about it (metadata + its notes). Empty values are
 * left out.
 */
export function sourceRequestView(
  meta: ExternalRequestMeta,
  caseName: string,
  caseNotes: string | undefined,
  detail: ExternalRequestDetail | null,
): SourceRequestView {
  const date = (v?: string | null) => (v ? formatDate(v) : '');
  const rows: [string, string | null | undefined][] = [
    ['Request #', detail?.number || meta.external_request_id],
    ['Status', detail?.status ?? meta.request_status],
    ['Requester', detail?.requester ?? meta.requester],
    ['Submitted', date(detail?.submitted_at)],
    ['Due', date(detail?.due_date ?? meta.due_date)],
    ['Source', providerName(detail?.provider || meta.request_provider)],
  ];
  return {
    title: detail?.title || caseName,
    rows: rows.filter(([, v]) => v).map(([label, value]) => ({ label, value: String(value) })),
    text: detail?.content || caseNotes || '',
    url: detail?.url || null,
    stale: detail === null,
  };
}

const PROVIDER_NAMES: Record<string, string> = {
  nextrequest: 'NextRequest',
  mock: 'NextRequest (dev mock)',
};

/** Display name of a request provider ("nextrequest" → "NextRequest"). */
export function providerName(provider?: string): string | undefined {
  if (!provider) return undefined;
  return PROVIDER_NAMES[provider.toLowerCase()] ?? provider;
}

const EDISCOVERY_NAMES: Record<string, string> = {
  purview: 'Microsoft Purview',
  jatheon: 'Jatheon',
  mock: 'Mock (demo)',
};

/** Display name of an eDiscovery system ("purview" → "Microsoft Purview"). */
export function ediscoverySystemName(system?: string): string | undefined {
  if (!system) return undefined;
  return EDISCOVERY_NAMES[system.toLowerCase()] ?? system;
}

/**
 * eDiscovery systems that have delivered files to a case (FB-1021).
 * Prefers the first-class list field; falls back to the flag stamped on metadata.
 */
export function getEDiscoverySystems(caseData: {
  ediscovery_systems?: string[] | null;
  metadata?: Record<string, unknown> | null;
}): string[] {
  const fromField = caseData.ediscovery_systems;
  if (Array.isArray(fromField) && fromField.length > 0) {
    return uniqueSystems(fromField);
  }
  const raw = caseData.metadata?.ediscovery_systems;
  if (Array.isArray(raw)) {
    return uniqueSystems(raw.filter((s): s is string => typeof s === 'string'));
  }
  return [];
}

function uniqueSystems(systems: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const system of systems) {
    const key = system.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}
